<template>
  <div>
    <div class="row wrapper border-bottom white-bg page-heading">
      <div class="col-lg-12">
        <div class="ibox">
          <div class="ibox-title">
            <h2>Szczegóły wiadomości</h2>
            <ol class="breadcrumb">
              <li><router-link to="/">Home</router-link></li>
              <li>
                <router-link to="/allegro/accounts/list">
                  Allegro - Moje konta
                </router-link>
              </li>
              <li v-if="threadData.account?.pk">
                <router-link
                  :to="`/allegro/accounts/${threadData.account.pk}/overview`"
                >
                  {{ threadData.account.name || "-" }}
                </router-link>
              </li>
              <li v-if="threadData.account?.pk">
                <router-link
                  :to="`/allegro/accounts/${threadData.account.pk}/threads`"
                >
                  Wiadomości
                </router-link>
              </li>
              <li class="active"><strong>Szczegóły wiadomości</strong></li>
            </ol>
            <div class="ibox-tools"></div>
          </div>
        </div>
      </div>
    </div>

    <div class="wrapper wrapper-content animated fadeInRight">
      <div class="row">
        <div class="col-lg-12">
          <div class="ibox float-e-margins">
            <div class="ibox-title">
              <h5>Szczegóły wątku</h5>
              <div class="ibox-tools"></div>
            </div>
            <div class="ibox-content panel">
              <div class="panel-body">
                <dl class="dl-horizontal dl-order-detail">
                  <dt>Klient:</dt>
                  <dd>
                    {{
                      threadData?.client?.user?.full_name ||
                      threadData.client_name ||
                      "-"
                    }}
                  </dd>
                  <dt>E-mail klienta:</dt>
                  <dd>
                    {{
                      threadData?.client?.user?.email ||
                      threadData.client_email ||
                      "-"
                    }}
                  </dd>
                  <dt>Data ostatniej widomości:</dt>
                  <dd>{{ threadData.last_message_date_time || "-" }}</dd>
                  <dt>Allegro ID wątku:</dt>
                  <dd>{{ threadData.external_id || "-" }}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-lg-12">
          <div class="ibox">
            <div class="ibox-title">
              <h5>Wiadomości</h5>
              <div class="ibox-tools"></div>
            </div>
            <div class="ibox-content">
              <ol class="chat">
                <div class="day">
                  {{ threadData.last_message_date_time || "" }}
                </div>
                <li
                  v-for="message in threadMessages"
                  :key="message.pk || message.external_id || message.created_at"
                  :class="{
                    self: message?.author?.is_admin,
                    other: !message?.author?.is_admin,
                  }"
                >
                  <div class="avatar"></div>
                  <div class="msg">
                    <strong
                      v-if="message.subject"
                      v-html="message.subject"
                    ></strong>
                    <p v-html="message.text || message.content"></p>
                    <p>
                      <template
                        v-for="(file, fileIndex) in message.files ||
                        message.message_files ||
                        []"
                        :key="file.pk || file.url || file.file || fileIndex"
                      >
                        <span v-if="file.is_removed" class="text-muted">
                          {{ file.name }} <small>(plik usunięty)</small>
                        </span>
                        <a v-else :href="file.url || file.file" target="_blank">
                          {{ file.name }}
                        </a>
                        <br />
                      </template>
                    </p>
                    <time>{{ message.created_at || message.date_added }}</time>
                    <span class="author">
                      {{ message?.author?.full_name || message.user_username }}
                    </span>
                    <span class="author">{{
                      message?.author?.email || ""
                    }}</span>
                  </div>
                </li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      <div class="row" v-if="canAddThreadMessage">
        <div class="col-lg-12">
          <div class="ibox float-e-margins">
            <div class="ibox-title">
              <h5>Wyślij odpowiedź</h5>
            </div>
            <div class="ibox-content">
              <div class="container-fields">
                <div class="form-group">
                  <label class="col-sm-2 control-label">Treść</label>
                  <div class="col-sm-10">
                    <textarea
                      v-model="messageContent"
                      class="textarea"
                      placeholder="Kliknij aby zacząć pisać"
                      required
                    ></textarea>
                  </div>
                </div>
              </div>
              <div class="m-t-sm">
                <label class="btn btn-info btn-file">
                  {{ selectedFiles.length ? "Zmień pliki" : "Dodaj pliki" }}
                  <input
                    ref="filesInput"
                    type="file"
                    multiple
                    @change="onFileChange"
                    style="display: none"
                  />
                </label>
                <button
                  v-if="selectedFiles.length"
                  @click="clearFiles"
                  class="btn btn-default m-l-sm"
                >
                  Wyczyść
                </button>
              </div>
              <ul class="list-group m-t-sm">
                <li
                  v-for="(file, index) in selectedFiles"
                  :key="index"
                  class="list-group-item"
                >
                  {{ file.name }}
                </li>
              </ul>
              <div v-if="messageError" class="has-error m-t-sm">
                <span class="help-block">{{ messageError }}</span>
              </div>
              <div class="container-btns">
                <button
                  @click="sendMessage"
                  class="btn btn-primary submit-btn"
                  :disabled="!messageContent || isSending"
                >
                  {{ isSending ? "Wysyłanie..." : "Wyślij" }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { useStore } from "vuex";
import { hasPermission } from "../../permissions";
import {
  allegroThreadPermissionsConst,
  allegroThreadMessagesConst,
} from "../../constants/allegroConstants";

// The field names carry no meaning for the user, only the message does
const getMessageError = (data) => {
  const entries = Object.entries(data || {});
  if (!entries.length) {
    return "Nie udało się wysłać wiadomości.";
  }
  const value = entries[0][1];
  return Array.isArray(value) ? value[0] : String(value);
};

export default {
  name: "AllegroThreadDetails",
  setup() {
    const route = useRoute();
    const store = useStore();
    const threadData = ref({});

    // --- Current admin permissions ---
    const canAddThreadMessage = computed(() =>
      hasPermission(allegroThreadPermissionsConst.add)
    );
    // --- End: current admin permissions ---
    const messageContent = ref("");
    const selectedFiles = ref([]);
    const filesInput = ref(null);
    const isSending = ref(false);
    const messageError = ref(null);
    const rawThreadData = computed(() => store.getters.getAllegroThreadData);
    const threadMessages = computed(() => threadData.value.messages || []);
    const sendData = computed(
      () => store.getters.getAllegroThreadMessageCreateData
    );

    const onFileChange = (e) => {
      messageError.value = null;
      selectedFiles.value = Array.from(e.target.files || []);
    };

    // Resets the native input as well, otherwise picking the same file again
    // raises no change event
    const clearFiles = () => {
      selectedFiles.value = [];
      if (filesInput.value) {
        filesInput.value.value = "";
      }
    };

    const sendMessage = () => {
      if (!canAddThreadMessage.value) {
        return;
      }
      if (!messageContent.value || isSending.value) {
        return;
      }
      messageError.value = null;
      isSending.value = true;
      store.dispatch("createAllegroThreadMessage", {
        pk: route.params.pk,
        text: messageContent.value,
        upload_files: selectedFiles.value,
      });
    };

    store.dispatch("getAllegroThread", route.params.pk);

    watch(
      () => rawThreadData.value,
      (newValue) => {
        if (newValue?.status === 200) {
          threadData.value = newValue.data;
        }
      },
      { deep: true }
    );

    watch(
      () => sendData.value,
      (newValue) => {
        isSending.value = false;

        if (newValue?.status === 200 || newValue?.status === 201) {
          messageContent.value = "";
          messageError.value = null;
          clearFiles();
          store.dispatch("showToaster", {
            type: "success",
            content: allegroThreadMessagesConst.add,
          });
          store.dispatch("getAllegroThread", route.params.pk);
        } else if (newValue?.data) {
          // The server reports the file type and the 5 MB limit here, so the
          // message is shown at the form as well, not only as a toast
          messageError.value = getMessageError(newValue.data);
          store.dispatch("showToaster", {
            type: "error",
            content: messageError.value,
          });
        }
      },
      { deep: true }
    );

    return {
      route,
      threadData,
      threadMessages,
      messageContent,
      selectedFiles,
      filesInput,
      isSending,
      messageError,
      onFileChange,
      clearFiles,
      sendMessage,
      canAddThreadMessage,
    };
  },
};
</script>
